import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import SecurityHeader from '../../components/ui/SecurityHeader';
import BottomTabNavigation from '../../components/ui/BottomTabNavigation';
import NotificationBanner from '../../components/ui/NotificationBanner';
import AlertCard from './components/AlertCard';
import NotificationSettings from './components/NotificationSettings';
import AlertFilters from './components/AlertFilters';
import AlertHistory from './components/AlertHistory';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const AlertsNotifications = () => {
  const [alerts, setAlerts] = useState([]);
  const [filters, setFilters] = useState({
    severity: 'all',
    status: 'all',
    device: 'all',
    dateRange: '24h',
    customStartDate: '',
    customEndDate: '',
    searchTerm: ''
  });
  const [isConnected, setIsConnected] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Mock active alerts data
  const mockAlerts = [
    {
      id: 'ALT-001',
      timestamp: new Date(Date.now() - 300000), // 5 minutes ago
      severity: 'critical',
      status: 'active',
      title: 'Unauthorized Access Attempt',
      description: 'Multiple failed login attempts detected from IP 192.168.1.100',
      device: 'SL-001',
      userId: 'USR-456',
      behavioralFlag: 'Access attempt outside normal hours (2:30 AM)'
    },
    {
      id: 'ALT-002',
      timestamp: new Date(Date.now() - 600000), // 10 minutes ago
      severity: 'high',
      status: 'acknowledged',
      title: 'Device Communication Lost',
      description: 'Smart lock SL-003 has lost network connectivity',
      device: 'SL-003',
      userId: null,
      behavioralFlag: null
    },
    {
      id: 'ALT-003',
      timestamp: new Date(Date.now() - 900000), // 15 minutes ago
      severity: 'medium',
      status: 'investigating',
      title: 'Unusual Access Pattern',
      description: 'User USR-789 accessed 3 different doors within 2 minutes',
      device: 'SL-002',
      userId: 'USR-789',
      behavioralFlag: 'Rapid sequential access pattern detected'
    },
    {
      id: 'ALT-004',
      timestamp: new Date(Date.now() - 1200000), // 20 minutes ago
      severity: 'low',
      status: 'active',
      title: 'Battery Level Warning',
      description: 'Device battery level is below 15%',
      device: 'SL-004',
      userId: null,
      behavioralFlag: null
    },
    {
      id: 'ALT-005',
      timestamp: new Date(Date.now() - 1800000), // 30 minutes ago
      severity: 'critical',
      status: 'active',
      title: 'Physical Tampering Detected',
      description: 'Accelerometer detected unusual vibrations on lock mechanism',
      device: 'SL-005',
      userId: null,
      behavioralFlag: 'Physical manipulation attempt'
    }
  ];

  useEffect(() => {
    // Initialize alerts
    setAlerts(mockAlerts);

    // Simulate WebSocket connection for real-time updates
    const interval = setInterval(() => {
      setLastUpdate(new Date());
      
      // Randomly add new alerts or update existing ones
      if (Math.random() < 0.3) {
        const newAlert = {
          id: `ALT-${Date.now()}`,
          timestamp: new Date(),
          severity: ['critical', 'high', 'medium', 'low']?.[Math.floor(Math.random() * 4)],
          status: 'active',
          title: 'New Security Event',
          description: 'Real-time security alert detected',
          device: `SL-00${Math.floor(Math.random() * 5) + 1}`,
          userId: Math.random() < 0.5 ? `USR-${Math.floor(Math.random() * 1000)}` : null,
          behavioralFlag: Math.random() < 0.4 ? 'Anomalous behavior detected' : null
        };
        
        setAlerts(prev => [newAlert, ...prev?.slice(0, 9)]);
      }
    }, 15000);

    // Simulate connection status changes
    const connectionInterval = setInterval(() => {
      setIsConnected(prev => Math.random() < 0.9 ? true : !prev);
    }, 30000);

    return () => {
      clearInterval(interval);
      clearInterval(connectionInterval);
    };
  }, []);

  const handleAlertAction = (alertId, action) => {
    setAlerts(prev => prev?.map(alert => {
      if (alert?.id === alertId) {
        switch (action) {
          case 'acknowledge':
            return { ...alert, status: 'acknowledged' };
          case 'investigate':
            return { ...alert, status: 'investigating' };
          case 'resolve':
            return { ...alert, status: 'resolved' };
          default:
            return alert;
        }
      }
      return alert;
    }));
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const filteredAlerts = alerts?.filter(alert => {
    if (filters?.severity !== 'all' && alert?.severity !== filters?.severity) return false;
    if (filters?.status !== 'all' && alert?.status !== filters?.status) return false;
    if (filters?.device !== 'all' && alert?.device !== filters?.device) return false;
    if (filters?.searchTerm) {
      const searchLower = filters?.searchTerm?.toLowerCase();
      if (!alert?.title?.toLowerCase()?.includes(searchLower) &&
          !alert?.description?.toLowerCase()?.includes(searchLower) &&
          !alert?.device?.toLowerCase()?.includes(searchLower)) {
        return false;
      }
    }
    return true;
  });

  const getAlertStats = () => {
    const stats = {
      total: filteredAlerts?.length,
      critical: filteredAlerts?.filter(a => a?.severity === 'critical')?.length,
      active: filteredAlerts?.filter(a => a?.status === 'active')?.length,
      acknowledged: filteredAlerts?.filter(a => a?.status === 'acknowledged')?.length
    };
    return stats;
  };

  const stats = getAlertStats();

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Alerts & Notifications - SecureLock Admin</title>
        <meta name="description" content="Manage security alerts and notification preferences for IoT access control system" />
      </Helmet>
      <SecurityHeader />
      <NotificationBanner />
      <main className="pt-16 pb-20 px-4 max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-2">Alerts & Notifications</h1>
              <p className="text-muted-foreground">
                Monitor security events and manage notification preferences
              </p>
            </div>
            
            {/* Connection Status */}
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-success security-pulse' : 'bg-error'}`} />
              <span className={`text-sm ${isConnected ? 'text-success' : 'text-error'}`}>
                {isConnected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-card border border-border rounded-lg p-4 security-shadow-card">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-accent/20 rounded-lg flex items-center justify-center">
                  <Icon name="Bell" size={16} className="text-accent" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats?.total}</p>
                  <p className="text-xs text-muted-foreground">Total Alerts</p>
                </div>
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-4 security-shadow-card">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-error/20 rounded-lg flex items-center justify-center">
                  <Icon name="AlertTriangle" size={16} className="text-error" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-error">{stats?.critical}</p>
                  <p className="text-xs text-muted-foreground">Critical</p>
                </div>
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-4 security-shadow-card">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-warning/20 rounded-lg flex items-center justify-center">
                  <Icon name="Clock" size={16} className="text-warning" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-warning">{stats?.active}</p>
                  <p className="text-xs text-muted-foreground">Active</p>
                </div>
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-4 security-shadow-card">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-success/20 rounded-lg flex items-center justify-center">
                  <Icon name="CheckCircle" size={16} className="text-success" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-success">{stats?.acknowledged}</p>
                  <p className="text-xs text-muted-foreground">Acknowledged</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Active Alerts */}
          <div className="lg:col-span-2 space-y-6">
            {/* Alert Filters */}
            <AlertFilters onFiltersChange={handleFiltersChange} />

            {/* Active Alerts Feed */}
            <div className="bg-card border border-border rounded-lg security-shadow-card">
              <div className="flex items-center justify-between p-4 border-b border-border">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-error/20 rounded-lg flex items-center justify-center">
                    <Icon name="AlertTriangle" size={16} className="text-error" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">Active Alerts</h2>
                    <p className="text-sm text-muted-foreground">
                      Last updated: {lastUpdate?.toLocaleTimeString()}
                    </p>
                  </div>
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  iconName="RefreshCw"
                  onClick={() => setLastUpdate(new Date())}
                  className="text-xs"
                >
                  Refresh
                </Button>
              </div>

              <div className="p-4">
                {filteredAlerts?.length > 0 ? (
                  <div className="space-y-4">
                    {filteredAlerts?.map((alert) => (
                      <AlertCard
                        key={alert?.id}
                        alert={alert}
                        onAcknowledge={(id) => handleAlertAction(id, 'acknowledge')}
                        onInvestigate={(id) => handleAlertAction(id, 'investigate')}
                        onResolve={(id) => handleAlertAction(id, 'resolve')}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-foreground mb-2">All Clear!</h3>
                    <p className="text-muted-foreground">No active alerts match your current filters.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Settings & History */}
          <div className="space-y-6">
            {/* Notification Settings */}
            <NotificationSettings />

            {/* Quick Actions */}
            <div className="bg-card border border-border rounded-lg security-shadow-card p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3">Quick Actions</h3>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  iconName="CheckCircle"
                  onClick={() => {
                    setAlerts(prev => prev?.map(alert => 
                      alert?.status === 'active' ? { ...alert, status: 'acknowledged' } : alert
                    ));
                  }}
                >
                  Acknowledge All Active
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  iconName="Download"
                >
                  Export Alert Report
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  iconName="Settings"
                >
                  Configure Thresholds
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Alert History Section */}
        <div className="mt-8">
          <AlertHistory filters={filters} />
        </div>
      </main>
      <BottomTabNavigation />
    </div>
  );
};

export default AlertsNotifications;